import React from 'react';
import { Button, Spin } from 'antd';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import SettingsCard from '../../SessionSettingsShared/components/SettingsCard';
import type { BenchmarkSuggestion } from 'state';
import { LibOption, round1 } from './helpers';

const SUGGESTION_PENDING = 0;
const SUGGESTION_COMPUTED = 1;
const SUGGESTION_ERROR = 2;
const SUGGESTION_CANCELED = 3;

interface Props {
  suggestion?: BenchmarkSuggestion | null;
  waiting?: boolean;
  disabledReason?: string;
  optionIndex: Record<string, LibOption>;
  onGenerate: () => void;
  onApply: (suggestion: BenchmarkSuggestion) => void;
}

const statusLabel = (suggestion?: BenchmarkSuggestion | null): string => {
  if (!suggestion) return 'No suggestion yet';
  if (suggestion.stale || suggestion.is_stale) return 'Stale';
  switch (suggestion.status) {
    case SUGGESTION_PENDING:
      return 'Generating';
    case SUGGESTION_COMPUTED:
      return 'Ready';
    case SUGGESTION_CANCELED:
      return 'Canceled';
    case SUGGESTION_ERROR:
      return 'Error';
    default:
      return 'No suggestion yet';
  }
};

const BenchmarkSuggestionV2: React.FC<Props> = ({
  suggestion,
  waiting,
  disabledReason,
  optionIndex,
  onGenerate,
  onApply,
}) => {
  const isPending = waiting || suggestion?.status === SUGGESTION_PENDING;
  const isComputed = suggestion?.status === SUGGESTION_COMPUTED;
  const isStale = !!(suggestion?.stale || suggestion?.is_stale);
  const hasBasket = isComputed && (suggestion?.components?.length ?? 0) > 0;
  const canGenerate = !disabledReason && !isPending;
  const generateLabel = suggestion ? 'Regenerate' : 'Generate';

  const extra = (
    <span
      className={`bmv2-suggest-status${
        isStale ? ' is-warn' : isComputed ? ' is-ok' : ''
      }`}
    >
      {isStale ? (
        <ExclamationCircleOutlined />
      ) : isComputed ? (
        <CheckCircleOutlined />
      ) : null}
      {statusLabel(suggestion)}
    </span>
  );

  let body: React.ReactNode;
  if (isPending) {
    body = (
      <div className="bmv2-suggest-empty">
        <Spin />
        <span>Generating suggestion...</span>
      </div>
    );
  } else if (
    !suggestion ||
    suggestion.status === SUGGESTION_CANCELED ||
    suggestion.status === SUGGESTION_ERROR
  ) {
    body = (
      <div className="bmv2-suggest-empty">
        <ThunderboltOutlined className="bmv2-empty__icon" />
        <span>
          {disabledReason || 'Generate a benchmark basket from this universe.'}
        </span>
      </div>
    );
  } else if (!hasBasket) {
    body = (
      <div className="bmv2-suggest-empty">
        <ExclamationCircleOutlined className="bmv2-empty__icon" />
        <span>No matching index-fund basket was found.</span>
      </div>
    );
  } else {
    body = (
      <div className="bmv2-suggest-list">
        {suggestion.components.map(component => {
          const meta = optionIndex[component.ric];
          return (
            <div
              className="bmv2-suggest-row"
              key={`${component.ric}-${component.id}`}
            >
              <div className="bmv2-suggest-row__main">
                <span className="bmv2-suggest-row__title">
                  {meta?.name ?? component.ric}
                </span>
                <span className="bmv2-brow-meta">
                  <span className="bmv2-ric">{component.ric}</span>
                  {meta?.assetClass ? (
                    <span className="bmv2-tag">{meta.assetClass}</span>
                  ) : null}
                  {meta?.geography ? (
                    <span className="bmv2-tag">{meta.geography}</span>
                  ) : null}
                </span>
              </div>
              <span className="bmv2-suggest-row__weight">
                {round1(component.weight * 100)}%
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  const footer = (
    <div className="bmv2-foot">
      <div
        className={`bmv2-foot__msg${disabledReason || isStale ? ' is-warn' : ''}`}
      >
        {isStale ? 'Universe/coverage changed' : disabledReason || ''}
      </div>
      <div className="bmv2-foot__actions">
        {hasBasket ? (
          <Button
            type="primary"
            size="small"
            disabled={isPending}
            onClick={() => suggestion && onApply(suggestion)}
          >
            Apply
          </Button>
        ) : null}
        <Button
          type={hasBasket ? 'text' : 'primary'}
          size="small"
          icon={suggestion ? <ReloadOutlined /> : <ThunderboltOutlined />}
          loading={isPending}
          disabled={!canGenerate}
          onClick={onGenerate}
        >
          {generateLabel}
        </Button>
      </div>
    </div>
  );

  return (
    <SettingsCard
      title="Suggested benchmark"
      className="bmv2-card bmv2-suggest-card"
      extra={extra}
      footer={footer}
    >
      {body}
    </SettingsCard>
  );
};

export default BenchmarkSuggestionV2;
